import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, UserPlus } from 'lucide-react';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import { validateCPF, validateEmail, validateRequired, validateDate, validateMinAge, validatePhone } from '../utils/validators';
import { maskCPF, maskCEP, maskPhone } from '../utils/masks';
import { removeMask, formatCurrency } from '../utils/formatters';
import { Plan } from '../types';
import { cepService } from '../services/cepService';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://fabio-ubuntu:8082/api';

const PublicRegister: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    data_nascimento: '',
    email: '',
    password: '',
    password_confirmation: '',
    telefone: '',
    celular: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    payment_day: 10,
    plan_id: undefined as number | undefined,
    is_hospital_employee: false,
    employee_cpf: '',
    employee_registration: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [checkingCpf, setCheckingCpf] = useState(false);
  const [cpfExists, setCpfExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [loadingCEP, setLoadingCEP] = useState(false);

  useEffect(() => {
    loadAvailablePlans();
  }, []);

  const loadAvailablePlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/plans/active`);
      setAvailablePlans(response.data);
    } catch (err: any) {
      console.error('Erro ao carregar planos disponíveis');
    }
  };

  const checkCpfExists = async (cpf: string) => {
    if (!validateCPF(cpf)) {
      return; // Não verifica se o CPF é inválido
    }

    setCheckingCpf(true);
    setCpfExists(false);

    try {
      const response = await axios.get(`${API_URL}/associados/check-cpf/${removeMask(cpf)}`);

      if (response.data.exists) {
        setCpfExists(true);
        setErrors(prev => ({ ...prev, cpf: 'Este CPF já está cadastrado no sistema' }));

        // Mostra toast de erro
        toast.error('Este CPF já está cadastrado no sistema!', {
          duration: 4000,
          position: 'top-center',
          icon: '⚠️',
          style: {
            background: '#ef4444',
            color: '#fff',
            fontWeight: '500',
            padding: '16px',
            borderRadius: '10px',
          },
        });
      } else {
        setCpfExists(false);
        if (errors.cpf === 'Este CPF já está cadastrado no sistema') {
          setErrors(prev => ({ ...prev, cpf: '' }));
        }
      }
    } catch (err: any) {
      console.error('Erro ao verificar CPF:', err);
    } finally {
      setCheckingCpf(false);
    }
  };

  const checkEmailExists = async (email: string) => {
    if (!validateEmail(email)) {
      return; // Não verifica se o email é inválido
    }

    setCheckingEmail(true);
    setEmailExists(false);

    try {
      const response = await axios.get(`${API_URL}/associados/check-email/${email}`);

      if (response.data.exists) {
        setEmailExists(true);
        setErrors(prev => ({ ...prev, email: 'Este e-mail já está cadastrado no sistema' }));

        // Mostra toast de erro
        toast.error('Este e-mail já está cadastrado no sistema!', {
          duration: 4000,
          position: 'top-center',
          icon: '⚠️',
          style: {
            background: '#ef4444',
            color: '#fff',
            fontWeight: '500',
            padding: '16px',
            borderRadius: '10px',
          },
        });
      } else {
        setEmailExists(false);
        if (errors.email === 'Este e-mail já está cadastrado no sistema') {
          setErrors(prev => ({ ...prev, email: '' }));
        }
      }
    } catch (err: any) {
      console.error('Erro ao verificar e-mail:', err);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleCEPBlur = async () => {
    const cep = formData.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;

    setLoadingCEP(true);
    try {
      const data = await cepService.buscarCep(cep);
      if (data) {
        setFormData((prev) => ({
          ...prev,
          endereco: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf,
        }));
      }
    } catch (err) {
      console.error('Erro ao buscar CEP:', err);
    } finally {
      setLoadingCEP(false);
    }
  };

  const validateFieldOnBlur = (fieldName: string, value: any) => {
    let error = '';

    switch (fieldName) {
      case 'data_nascimento':
        if (value && !validateDate(value)) {
          error = 'Data de nascimento inválida';
        } else if (value && !validateMinAge(value, 18)) {
          error = 'Você deve ter no mínimo 18 anos';
        }
        break;

      case 'email':
        if (value && !validateEmail(value)) {
          error = 'Email inválido';
        }
        break;

      case 'telefone':
        if (value && !validatePhone(value)) {
          error = 'Telefone inválido. Use o formato: (00) 0000-0000 ou (00) 00000-0000';
        }
        break;

      case 'celular':
        if (value && !validatePhone(value)) {
          error = 'Celular inválido. Use o formato: (00) 00000-0000';
        }
        break;

      default:
        break;
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [fieldName]: error }));
    } else {
      setErrors((prev) => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    let maskedValue: any = value;

    // Lidar com checkboxes
    if (type === 'checkbox') {
      maskedValue = (e.target as HTMLInputElement).checked;

      // Se desmarcar "é funcionário do hospital", limpar os campos relacionados
      if (name === 'is_hospital_employee' && !maskedValue) {
        setFormData((prev) => ({
          ...prev,
          [name]: maskedValue,
          employee_cpf: '',
          employee_registration: '',
        }));
        return;
      }
    } else if (name === 'cpf' || name === 'employee_cpf') {
      maskedValue = maskCPF(value);
    } else if (name === 'cep') {
      maskedValue = maskCEP(value);
    } else if (name === 'telefone' || name === 'celular') {
      maskedValue = maskPhone(value);
    } else if (name === 'plan_id') {
      maskedValue = value ? Number(value) : undefined;
    }

    setFormData((prev) => ({ ...prev, [name]: maskedValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(formData.nome)) newErrors.nome = 'Nome é obrigatório';

    if (!validateRequired(formData.cpf)) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (!validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    } else if (cpfExists) {
      newErrors.cpf = 'Este CPF já está cadastrado no sistema';
    }

    if (!validateRequired(formData.data_nascimento)) {
      newErrors.data_nascimento = 'Data de nascimento é obrigatória';
    } else if (!validateDate(formData.data_nascimento)) {
      newErrors.data_nascimento = 'Data de nascimento inválida';
    } else if (!validateMinAge(formData.data_nascimento, 18)) {
      newErrors.data_nascimento = 'Você deve ter no mínimo 18 anos';
    }

    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email é obrigatório';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    } else if (emailExists) {
      newErrors.email = 'Este e-mail já está cadastrado no sistema';
    }

    if (!validateRequired(formData.password)) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Senha deve ter no mínimo 6 caracteres';
    }

    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'As senhas não conferem';
    }

    if (!validateRequired(formData.telefone)) {
      newErrors.telefone = 'Telefone é obrigatório';
    } else if (!validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido. Use o formato: (00) 0000-0000 ou (00) 00000-0000';
    }

    if (formData.celular && !validatePhone(formData.celular)) {
      newErrors.celular = 'Celular inválido. Use o formato: (00) 00000-0000';
    }

    if (!validateRequired(formData.cep)) newErrors.cep = 'CEP é obrigatório';
    if (!validateRequired(formData.endereco)) newErrors.endereco = 'Endereço é obrigatório';
    if (!validateRequired(formData.numero)) newErrors.numero = 'Número é obrigatório';
    if (!validateRequired(formData.bairro)) newErrors.bairro = 'Bairro é obrigatório';
    if (!validateRequired(formData.cidade)) newErrors.cidade = 'Cidade é obrigatória';
    if (!validateRequired(formData.estado)) newErrors.estado = 'Estado é obrigatório';

    // Validar campos de funcionário do hospital se marcado
    if (formData.is_hospital_employee) {
      if (!validateRequired(formData.employee_cpf)) {
        newErrors.employee_cpf = 'CPF do funcionário é obrigatório';
      } else if (!validateCPF(formData.employee_cpf)) {
        newErrors.employee_cpf = 'CPF do funcionário inválido';
      }

      if (!validateRequired(formData.employee_registration)) {
        newErrors.employee_registration = 'Matrícula do funcionário é obrigatória';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        cpf: removeMask(formData.cpf),
        cep: removeMask(formData.cep),
        telefone: removeMask(formData.telefone),
        celular: formData.celular ? removeMask(formData.celular) : undefined,
        employee_cpf: formData.is_hospital_employee && formData.employee_cpf ? removeMask(formData.employee_cpf) : undefined,
      };

      const response = await axios.post(`${API_URL}/associados/register`, dataToSend);

      setRegisteredEmail(response.data.email);
      setSuccess(true);

      // Salvar email em localStorage para permitir retorno à verificação
      localStorage.setItem('pendingEmailVerification', response.data.email);

      // Redirecionar após 3 segundos
      setTimeout(() => {
        navigate('/verificar-email', { state: { email: response.data.email } });
      }, 3000);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const serverErrors: Record<string, string> = {};
        Object.keys(err.response.data.errors).forEach(key => {
          serverErrors[key] = err.response.data.errors[key][0];
        });
        setErrors(serverErrors);
      } else {
        setError(err.response?.data?.message || 'Erro ao realizar cadastro. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <Toaster />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
          <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mx-auto h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Cadastro Realizado com Sucesso!
            </h2>
            <p className="text-gray-600 mb-6">
              Enviamos um código de verificação para <strong>{registeredEmail}</strong>
            </p>
            <p className="text-sm text-gray-500">
              Redirecionando para verificação...
            </p>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <Toaster />
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar</span>
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Cadastre-se</h2>
            <p className="mt-2 text-sm text-gray-600">
              Preencha seus dados para se tornar um associado
            </p>
          </div>

          {error && (
            <Alert variant="error" onClose={() => setError('')} className="mb-6">
              {error}
            </Alert>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Dados Pessoais */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados Pessoais</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input
                    label="Nome Completo"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    error={errors.nome}
                    required
                    disabled={loading}
                  />
                </div>

                <Input
                  label="CPF"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleChange}
                  onBlur={() => checkCpfExists(formData.cpf)}
                  error={errors.cpf}
                  placeholder="000.000.000-00"
                  required
                  disabled={loading || checkingCpf}
                />

                <Input
                  label="Data de Nascimento"
                  type="date"
                  name="data_nascimento"
                  value={formData.data_nascimento}
                  onChange={handleChange}
                  onBlur={() => validateFieldOnBlur('data_nascimento', formData.data_nascimento)}
                  error={errors.data_nascimento}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Vínculo com Hospital */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vínculo com Hospital</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_hospital_employee"
                    name="is_hospital_employee"
                    checked={formData.is_hospital_employee}
                    onChange={handleChange}
                    disabled={loading}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <label
                    htmlFor="is_hospital_employee"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    É funcionário do hospital?
                  </label>
                </div>

                {formData.is_hospital_employee && (
                  <div className="grid md:grid-cols-2 gap-4 pl-6">
                    <Input
                      label="CPF do Funcionário"
                      name="employee_cpf"
                      value={formData.employee_cpf}
                      onChange={handleChange}
                      error={errors.employee_cpf}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      required
                      disabled={loading}
                    />

                    <Input
                      label="Matrícula"
                      name="employee_registration"
                      value={formData.employee_registration}
                      onChange={handleChange}
                      error={errors.employee_registration}
                      placeholder="Número da matrícula"
                      required
                      disabled={loading}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Plano de Benefícios */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Plano de Benefícios</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecione o Plano (Opcional)
                </label>
                <select
                  name="plan_id"
                  value={formData.plan_id || ''}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Escolher depois</option>
                  {availablePlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.nome} - {formatCurrency(plan.valor)}/mês
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Você pode escolher um plano agora ou adicionar depois
                </p>
              </div>

              <div className="mt-4">
                <Input
                  label="Melhor dia para pagamento"
                  type="number"
                  name="payment_day"
                  value={formData.payment_day}
                  onChange={handleChange}
                  min="1"
                  max="31"
                  disabled={loading}
                  placeholder="10"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Informe o melhor dia do mês para realizar o pagamento (1 a 31)
                </p>
              </div>
            </div>

            {/* Contato */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contato</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => {
                      validateFieldOnBlur('email', formData.email);
                      checkEmailExists(formData.email);
                    }}
                    error={errors.email}
                    required
                    disabled={loading || checkingEmail}
                  />
                </div>

                <Input
                  label="Telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  onBlur={() => validateFieldOnBlur('telefone', formData.telefone)}
                  error={errors.telefone}
                  placeholder="(00) 00000-0000"
                  required
                  disabled={loading}
                />

                <Input
                  label="Celular (opcional)"
                  name="celular"
                  value={formData.celular}
                  onChange={handleChange}
                  onBlur={() => validateFieldOnBlur('celular', formData.celular)}
                  error={errors.celular}
                  placeholder="(00) 00000-0000"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Senha */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Senha de Acesso</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Senha"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={errors.password}
                  placeholder="Mínimo 6 caracteres"
                  required
                  disabled={loading}
                />

                <Input
                  label="Confirmar Senha"
                  type="password"
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  error={errors.password_confirmation}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Endereço */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Endereço</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="CEP"
                  name="cep"
                  value={formData.cep}
                  onChange={handleChange}
                  onBlur={handleCEPBlur}
                  error={errors.cep}
                  placeholder="00000-000"
                  required
                  disabled={loading || loadingCEP}
                />

                <div className="md:col-span-2">
                  <Input
                    label="Endereço"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleChange}
                    error={errors.endereco}
                    required
                    disabled={loading || loadingCEP}
                  />
                </div>

                <Input
                  label="Número"
                  name="numero"
                  value={formData.numero}
                  onChange={handleChange}
                  error={errors.numero}
                  required
                  disabled={loading}
                />

                <Input
                  label="Complemento (opcional)"
                  name="complemento"
                  value={formData.complemento}
                  onChange={handleChange}
                  error={errors.complemento}
                  disabled={loading}
                />

                <Input
                  label="Bairro"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleChange}
                  error={errors.bairro}
                  required
                  disabled={loading || loadingCEP}
                />

                <Input
                  label="Cidade"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  error={errors.cidade}
                  required
                  disabled={loading || loadingCEP}
                />

                <Input
                  label="Estado"
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  error={errors.estado}
                  placeholder="UF"
                  maxLength={2}
                  required
                  disabled={loading || loadingCEP}
                />
              </div>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Cadastrando...' : 'Realizar Cadastro'}
              </Button>
            </div>

            <p className="text-center text-sm text-gray-600 mt-4">
              Já possui cadastro?{' '}
              <button
                type="button"
                onClick={() => navigate('/associado/login')}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Fazer login
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default PublicRegister;
